import { useQuery } from '@tanstack/react-query';
import { weatherService, type WeatherData, type WeatherError } from '@/lib/services/weatherService';
import { useState, useEffect } from 'react';

interface UseWeatherReturn {
  weatherData: WeatherData | null;
  isLoading: boolean;
  error: WeatherError | null;
}

export function useWeather(): UseWeatherReturn {
  const [coordinates, setCoordinates] = useState<{ lat: number; lon: number } | null>(null);

  useEffect(() => {
    // Get user's location
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCoordinates({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          // Default to a fallback location if needed
          setCoordinates({
            lat: 51.5074, // London coordinates as fallback
            lon: -0.1278,
          });
        }
      );
    }
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['weather', coordinates?.lat, coordinates?.lon],
    queryFn: async () => {
      if (!coordinates) {
        throw new Error('Location not available');
      }
      return weatherService.getForecast(coordinates.lat, coordinates.lon);
    },
    enabled: !!coordinates,
    staleTime: 1000 * 60 * 15, // 15 minutes
  });

  return {
    weatherData: data && !('message' in data) ? data : null,
    isLoading,
    error: data && 'message' in data ? data : error ? { message: error.message } : null,
  };
} 